import {
  ARTIFACTS,
  CHARACTERS,
  characterById,
  DEFAULT_CHARACTER,
  MAPS,
  MAX_EQUIPPED_ARTIFACTS,
  type MetaStats,
  meetsUnlock,
  unlockText,
} from "@roguelik/shared-sim";
import { motion } from "motion/react";
import { useState } from "preact/hooks";
import {
  act,
  authStatus,
  devUnlockAll,
  net,
  profile,
  roomMapId,
  roster,
  selectedCharacter,
} from "../store.js";
import { Tooltip } from "./Tooltip.js";

const USERNAME_RE = /^[A-Za-z0-9_-]{3,20}$/;

/** Rarity → accent color (shared by characters + artifacts grid). */
const RARITY_COLOR: Record<string, string> = {
  common: "#9fb2c8",
  rare: "#4aa3ff",
  epic: "#b26bff",
  legendary: "#ffb02e",
};

export function Lobby() {
  const n = net.value;
  if (n === null || n.runPhase !== "lobby") return null;
  if (selectedCharacter.value === "")
    selectedCharacter.value = profile.value?.character ?? DEFAULT_CHARACTER;
  const signedIn = profile.value !== null; // accounts are required to play (ADR-0024)

  // Dev/test escape hatch: click the title 5× to unlock every character + artifact.
  const [titleClicks, setTitleClicks] = useState(0);
  const onTitleClick = (): void => {
    if (devUnlockAll.value) return;
    const next = titleClicks + 1;
    setTitleClicks(next);
    if (next >= 5) {
      devUnlockAll.value = true;
      act()?.devUnlock();
    }
  };

  return (
    <motion.div
      class="pointer absolute inset-0 flex flex-col items-center justify-center gap-6 overflow-y-auto bg-gradient-to-b from-[#0a0f1a] to-[#070a12] p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div class="text-center">
        <button
          type="button"
          onClick={onTitleClick}
          class="cursor-default select-none bg-transparent text-4xl font-extrabold tracking-tight"
          title="roguelik.io"
        >
          roguelik.io
        </button>
        <p class="mt-1 text-sm opacity-70">
          Room {n.roomCode || "—"} · {n.players} player(s) · share the URL to co-op
        </p>
        {devUnlockAll.value && (
          <p class="mt-1 text-xs font-bold text-amber-300">
            DEV: all characters + artifacts unlocked
          </p>
        )}
      </div>

      <CharacterSelect />
      <MapSelect />
      <Account />
      <Roster />

      <motion.button
        type="button"
        disabled={!signedIn}
        whileHover={signedIn ? { scale: 1.05 } : {}}
        whileTap={signedIn ? { scale: 0.97 } : {}}
        onClick={() => signedIn && act()?.action("start")}
        class={`rounded-xl border px-10 py-3 text-lg font-bold tracking-wide ${
          signedIn
            ? "border-emerald-400/50 bg-emerald-500/20 hover:bg-emerald-500/30"
            : "cursor-not-allowed border-edge bg-panel/40 opacity-50"
        }`}
      >
        {signedIn ? "Start run" : "Sign in to play"}
      </motion.button>
    </motion.div>
  );
}

/** Who's in your room right now (lobby). */
function Roster() {
  const members = roster.value;
  if (members.length === 0) return null;
  // Match the worker's per-slot player palette (8 colors, cycled).
  const COLORS = [
    "#5ad1ff",
    "#ff7ab8",
    "#9b8cff",
    "#73e08a",
    "#ffd166",
    "#ff8c5a",
    "#6ee7d6",
    "#c792ea",
  ];
  return (
    <div class="flex w-[360px] flex-col gap-1.5 rounded-xl border border-edge bg-panel/70 p-4">
      <div class="text-xs font-bold uppercase tracking-widest opacity-50">
        In this room ({members.length})
      </div>
      {members.map((m) => (
        <div key={m.slot} class="flex items-center gap-2 text-sm">
          <span
            class="inline-block h-3 w-3 shrink-0 rounded-full"
            style={{ background: COLORS[m.slot % COLORS.length] }}
          />
          <span>{m.name}</span>
          {m.character !== undefined && (
            <span class="ml-auto text-xs opacity-60">
              {characterById(m.character)?.name ?? m.character}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Lobby map picker (Phase D): choose the room's arena layout. */
function MapSelect() {
  const a = act();
  const current = roomMapId.value;
  return (
    <div class="flex flex-col items-center gap-2">
      <div class="text-xs font-bold uppercase tracking-widest opacity-50">Map</div>
      <div class="flex flex-wrap justify-center gap-2">
        {MAPS.map((m) => {
          const active = m.id === current;
          return (
            <Tooltip
              key={m.id}
              width={180}
              content={
                <>
                  <div class="font-bold">{m.name}</div>
                  <div class="mt-0.5 opacity-80">
                    {m.walls.length === 0
                      ? "Open arena — no cover."
                      : `${m.walls.length} wall section(s)${m.objectives && m.objectives.length > 0 ? " + an objective zone" : ""}.`}
                  </div>
                </>
              }
            >
              <button
                type="button"
                onClick={() => a?.selectMap(m.id)}
                class={`rounded-md border-2 px-3 py-1.5 text-sm transition-colors ${
                  active
                    ? "border-sky-400 bg-sky-500/20 font-bold"
                    : "border-edge bg-panel/60 hover:border-sky-400/60"
                }`}
              >
                {m.name}
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

function CharacterSelect() {
  const p = profile.value;
  const meta: MetaStats = {
    totalKills: p?.totalKills ?? 0,
    bossKills: p?.bossKills ?? 0,
    bestLoop: p?.bestLoop ?? 0,
  };
  return (
    <div class="flex flex-col items-center gap-2">
      <div class="text-xs font-bold uppercase tracking-widest opacity-50">Character</div>
      <div class="flex flex-wrap justify-center gap-3">
        {CHARACTERS.map((c, i) => {
          const unlocked = devUnlockAll.value || (!c.locked && meetsUnlock(c.unlock, meta));
          const active = selectedCharacter.value === c.id;
          return (
            <motion.button
              key={c.id}
              type="button"
              disabled={!unlocked}
              title={unlocked ? c.blurb : `Locked — ${unlockText(c.unlock)}`}
              onClick={() => {
                if (!unlocked) return;
                selectedCharacter.value = c.id;
                act()?.selectCharacter(c.id);
              }}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i }}
              whileHover={unlocked ? { y: -4 } : {}}
              class={`relative flex w-44 flex-col gap-1 rounded-xl border-2 p-3 text-left transition-colors ${
                !unlocked
                  ? "cursor-not-allowed border-edge bg-panel/40 opacity-50"
                  : active
                    ? "border-emerald-400 bg-emerald-500/10"
                    : "border-edge bg-panel/70 hover:border-sky-400/60 cursor-pointer"
              }`}
            >
              <div class="flex items-center justify-between">
                <span class="font-bold">{c.name}</span>
                {!unlocked && <span class="text-xs opacity-70">🔒</span>}
                {active && unlocked && <span class="text-xs text-emerald-400">✓</span>}
              </div>
              <div class="text-xs leading-snug opacity-75">{c.blurb}</div>
              <div class="mt-1 flex items-center justify-between text-[10px] uppercase tracking-wide">
                <span style={{ color: RARITY_COLOR[c.rarity] }}>{c.rarity}</span>
                {c.ability !== "none" && (
                  <span class="rounded bg-white/10 px-1 py-0.5 font-semibold opacity-90">
                    ⚡ {c.ability}
                  </span>
                )}
              </div>
              {!unlocked && (
                <div class="text-[10px] leading-tight text-amber-300/80">
                  {unlockText(c.unlock)}
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

function Account() {
  const p = profile.value;
  const a = act();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const submit = (kind: "register" | "login"): void => {
    const u = username.trim();
    if (u === "" || password === "") {
      authStatus.value = "Enter a username and password.";
      return;
    }
    if (kind === "register" && !USERNAME_RE.test(u)) {
      authStatus.value = "Username: 3–20 letters, numbers, _ or - (no spaces).";
      return;
    }
    authStatus.value = kind === "register" ? "Creating account…" : "Logging in…";
    a?.auth({ kind, username: u, password });
  };

  return (
    <div class="flex w-[360px] flex-col gap-3 rounded-xl border border-edge bg-panel/70 p-4">
      {p === null ? (
        <>
          <div class="text-xs font-bold uppercase tracking-widest opacity-50">
            Account — required to play
          </div>
          <input
            placeholder="username"
            value={username}
            onInput={(e) => setUsername((e.target as HTMLInputElement).value)}
            class="rounded-md border border-edge bg-[#0c121e] px-3 py-2 text-sm outline-none focus:border-sky-400/60"
          />
          <input
            type="password"
            placeholder="password"
            value={password}
            onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
            class="rounded-md border border-edge bg-[#0c121e] px-3 py-2 text-sm outline-none focus:border-sky-400/60"
          />
          <div class="flex gap-2">
            <button
              type="button"
              onClick={() => submit("login")}
              class="flex-1 rounded-md border border-sky-400/40 bg-sky-500/15 py-1.5 text-sm hover:bg-sky-500/30"
            >
              Log in
            </button>
            <button
              type="button"
              onClick={() => submit("register")}
              class="flex-1 rounded-md border border-sky-400/40 bg-sky-500/15 py-1.5 text-sm hover:bg-sky-500/30"
            >
              Register
            </button>
          </div>
        </>
      ) : (
        <div class="flex items-center justify-between">
          <span class="text-sm">
            Signed in as <b>{p.username}</b> · ⛃ {p.currency}
          </span>
          <button
            type="button"
            onClick={() => a?.logout()}
            class="text-xs underline opacity-70 hover:opacity-100"
          >
            Log out
          </button>
        </div>
      )}
      {authStatus.value !== "" && (
        <div class="min-h-3.5 text-xs opacity-85">{authStatus.value}</div>
      )}
      {p !== null && <Shop />}
    </div>
  );
}

function Shop() {
  const p = profile.value;
  const a = act();
  if (p === null) return null;
  const owned = new Set(p.ownedArtifacts);
  const equipped = new Set(p.equippedArtifacts);
  const meta: MetaStats = {
    totalKills: p.totalKills ?? 0,
    bossKills: p.bossKills ?? 0,
    bestLoop: p.bestLoop ?? 0,
  };
  return (
    <div class="mt-1 flex max-h-72 flex-col gap-2 overflow-y-auto border-t border-edge pt-3">
      <div class="flex items-center gap-2 text-xs font-bold opacity-90">
        <span>Artifacts</span>
        <span class="flex gap-1">
          {Array.from({ length: MAX_EQUIPPED_ARTIFACTS }, (_, i) => (
            <span
              key={i}
              class={`inline-block h-2.5 w-2.5 rounded-full border ${
                i < equipped.size
                  ? "border-emerald-400 bg-emerald-400"
                  : "border-edge bg-transparent"
              }`}
            />
          ))}
        </span>
        <span class="opacity-70">
          {equipped.size}/{MAX_EQUIPPED_ARTIFACTS}
        </span>
        <span class="ml-auto opacity-70">⛃ {p.currency}</span>
      </div>
      {/* Icon grid: locked tiles greyed with the unlock requirement (item 9). */}
      <div class="grid grid-cols-3 gap-2">
        {ARTIFACTS.map((art) => {
          const isOwned = owned.has(art.id);
          const isEquipped = equipped.has(art.id);
          const unlocked = devUnlockAll.value || meetsUnlock(art.unlock, meta);
          const accent = RARITY_COLOR[art.rarity] ?? "#9fb2c8";
          const canBuy = unlocked && !isOwned && p.currency >= art.cost;
          const onClick = (): void => {
            if (!unlocked) return;
            if (!isOwned) {
              if (canBuy) a?.buy(art.id);
            } else {
              a?.equip(art.id, !isEquipped);
            }
          };
          const tip = (
            <>
              <div class="font-bold" style={{ color: accent }}>
                {art.icon} {art.name}
              </div>
              <div class="mt-0.5 opacity-85">{art.desc}</div>
              <div class="mt-1 text-[10px] uppercase tracking-wide opacity-60">{art.rarity}</div>
              <div class="mt-1 opacity-75">
                {!unlocked
                  ? `🔒 Locked — ${unlockText(art.unlock)}`
                  : isEquipped
                    ? "✓ Equipped — click to unequip"
                    : isOwned
                      ? "Click to equip"
                      : `Cost ⛃${art.cost}${canBuy ? " — click to buy" : " (not enough currency)"}`}
              </div>
            </>
          );
          return (
            <Tooltip key={art.id} content={tip} width={190}>
              <button
                type="button"
                disabled={!unlocked || (!isOwned && !canBuy)}
                onClick={onClick}
                class={`relative flex w-full flex-col items-center gap-1 rounded-lg border-2 p-2 text-center transition-colors ${
                  !unlocked
                    ? "cursor-not-allowed border-edge bg-panel/30 opacity-45"
                    : isEquipped
                      ? "bg-emerald-500/10"
                      : "bg-panel/60 hover:bg-panel"
                }`}
                style={{ borderColor: isEquipped ? "#34d399" : accent }}
              >
                <span class="text-2xl leading-none">{art.icon}</span>
                <span class="text-[11px] font-bold leading-tight">{art.name}</span>
                <span class="text-[9px] uppercase tracking-wide" style={{ color: accent }}>
                  {art.rarity}
                </span>
                <span class="text-[10px] opacity-80">
                  {!unlocked
                    ? "🔒"
                    : isEquipped
                      ? "✓ equipped"
                      : isOwned
                        ? "equip"
                        : `⛃${art.cost}`}
                </span>
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
